// mantine
import {
    Collapse,
    Divider,
    List,
    Paper,
    Title,
    UnstyledButton
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// components
import { NumberFormatter } from '@newo/ui-kit';
import { ChevronDown } from 'lucide-react';
import type { TSessionSummary } from '@newo/api/gateway';
import SummaryDivider from '@/pages/Report/components/Summary/components/SummaryDivider';
import StatItem from '@/pages/Report/components/StatItem';
import SummaryCard from '@/pages/Report/components/Summary/components/SummaryCard';
import HoursRevenue from '@/pages/Report/components/Summary/components/HoursRevenue';
import { getSessionsPageLinks } from '@/pages/Report/Report.utils';
// icons
// utils
import { hints } from '@/pages/Report/Report.data.tsx';
import { formatDate } from '@/utils/format';
import { useReportContext } from '@/pages/Report/Report.context';

interface SummaryProps {
    data: TSessionSummary;
}

const Summary = ({ data }: SummaryProps) => {
    const [isOpened, handlers] = useDisclosure(false);

    const ctx = useReportContext();
    const fromDate = formatDate(ctx.from_datetime ?? '', 'DD MMM, YYYY');
    const toDate = formatDate(ctx.to_datetime ?? '', 'DD MMM, YYYY');
    const {
        totalLeadCountLink,
        totalOpportunityCountLink,
        totalSessionsCountLink
    } = getSessionsPageLinks();

    const unitsWH = data.units.working_hours;
    const unitsNWH = data.units.non_working_hours;
    const totalUnits = unitsWH + unitsNWH;

    const asrWH = data.asr.working_hours;
    const asrNWH = data.asr.non_working_hours;
    const totalASR = asrWH + asrNWH;

    const additionalDealWH = data.additional_deal.working_hours;
    const additionalDealNWH = data.additional_deal.non_working_hours;
    const totalAdditionalDeal = additionalDealWH + additionalDealNWH;

    const revenueWH = data.revenue.working_hours;
    const revenueNWH = data.revenue.non_working_hours;
    const totalRevenue = revenueWH + revenueNWH;

    const leadsWH = data.leads.working_hours;
    const leadsNWH = data.leads.non_working_hours;
    const totalLeads = leadsWH + leadsNWH;

    const sessionsWH = data.sessions.working_hours;
    const sessionsNWH = data.sessions.non_working_hours;
    const totalSessions = sessionsWH + sessionsNWH;

    const opportunitySessionsWH = data.opportunity_sessions.working_hours;
    const opportunitySessionsNWH = data.opportunity_sessions.non_working_hours;
    const totalOpportunitySessions =
        opportunitySessionsWH + opportunitySessionsNWH;

    const opportunityValueWH = data.opportunity_value.working_hours;
    const opportunityValueNWH = data.opportunity_value.non_working_hours;
    const totalOpportunityValue = opportunityValueWH + opportunityValueNWH;

    const handleToggle = () => {
        handlers.toggle();
    };

    const rowClassName =
        'flex flex-col @sm:flex-row @sm:items-end @sm:justify-between gap-4';

    return (
        <Paper className="space-y-4 bg-gray-2 p-4 lg:p-6 print:p-0" radius="md">
            <Title order={3} size="h3">
                Executive Summary
            </Title>

            <div className="grid xl:grid-cols-[1fr_max-content_1fr_max-content_1fr_max-content_1fr] gap-3">
                <SummaryCard className="@container grid-rows-1 md:grid-rows-2">
                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.totalSessions}
                            title="Total Sessions Count"
                            to={totalSessionsCountLink}
                            value={totalSessions}
                        />
                        <HoursRevenue nwh={sessionsNWH} wh={sessionsWH} />
                    </div>
                    <Divider />
                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.totalUnits}
                            title="Total Units"
                            value={totalUnits}
                        />
                        <HoursRevenue nwh={unitsNWH} wh={unitsWH} />
                    </div>
                </SummaryCard>

                <SummaryDivider hint={hints.step1} />

                <SummaryCard className="@container">
                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.totalLeadCount}
                            title="Total Lead Count"
                            to={totalLeadCountLink}
                            value={totalLeads}
                        />
                        <HoursRevenue nwh={leadsNWH} wh={leadsWH} />
                    </div>

                    <Divider />

                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.totalLeadValue}
                            prefix="$"
                            title="Total Lead Value"
                            value={totalRevenue}
                        />
                        <HoursRevenue
                            nwh={revenueNWH}
                            prefix="$"
                            wh={revenueWH}
                        />
                    </div>
                </SummaryCard>

                <SummaryDivider hint={hints.step2} />

                <SummaryCard className="@container">
                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.totalOpportunityCount}
                            title="Total Opportunity Count"
                            to={totalOpportunityCountLink}
                            value={totalOpportunitySessions}
                        />
                        <HoursRevenue
                            nwh={opportunitySessionsNWH}
                            wh={opportunitySessionsWH}
                        />
                    </div>
                    <Divider />
                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.totalOpportunityValue}
                            prefix="$"
                            title="Total Opportunity Value"
                            value={totalOpportunityValue}
                        />
                        <HoursRevenue
                            nwh={opportunityValueNWH}
                            prefix="$"
                            wh={opportunityValueWH}
                        />
                    </div>
                </SummaryCard>

                <SummaryDivider hint={hints.step3} />

                <SummaryCard className="@container">
                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.totalAdditionalDealCount}
                            title="Additional Deal Count"
                            value={totalAdditionalDeal}
                            valueClassName="text-cyan-5"
                        />
                        <HoursRevenue
                            nwh={additionalDealNWH}
                            wh={additionalDealWH}
                        />
                    </div>
                    <Divider />
                    <div className={rowClassName}>
                        <StatItem
                            hint={hints.asr}
                            prefix="$"
                            title="Additional Secured Revenue"
                            value={totalASR}
                            valueClassName="text-green-5"
                        />
                        <HoursRevenue nwh={asrNWH} prefix="$" wh={asrWH} />
                    </div>
                </SummaryCard>
            </div>
            <div>
                <UnstyledButton
                    className="flex items-center gap-1 text-xs text-gray-6 mb-1"
                    onClick={handleToggle}
                >
                    Explanation <ChevronDown className="w-4 h-4" />
                </UnstyledButton>
                <Collapse in={isOpened}>
                    <List className="text-xs text-gray-6 leading-relaxed">
                        <List.Item>
                            Between <b>{fromDate}</b> and <b>{toDate}</b>, your
                            AI Employee handled{' '}
                            <b>
                                Total of{' '}
                                <NumberFormatter value={totalSessions} />{' '}
                                Sessions
                            </b>
                            . This includes all types of voice and chat sessions
                            — both with and without lead intent — excluding test
                            sessions.
                        </List.Item>
                        <List.Item>
                            Out of these Total Sessions, the <b>Total Leads</b>{' '}
                            amounted to{' '}
                            <b>
                                <NumberFormatter value={totalLeads} />
                            </b>
                            . This represents the number of sessions where the
                            client showed interest in booking or making a
                            purchase.
                        </List.Item>
                        <List.Item>
                            From those Leads, the <b>Total Opportunities</b> was{' '}
                            <b>
                                <NumberFormatter
                                    value={totalOpportunitySessions}
                                />
                            </b>{' '}
                            — a subset of leads where the client did not refuse
                            and there was an available booking slot or
                            actionable outcome.{' '}
                        </List.Item>
                        <List.Item>
                            Some of these Opportunities statistically convert
                            into Deals. However, a portion of those Deals{' '}
                            <b>only happened because of your AI Employee</b> —
                            for example, they occurred{' '}
                            <b>outside of business hours</b> or would have been
                            missed during working hours. We call these{' '}
                            <b>Additional Deals</b>, and their count for this
                            period was{' '}
                            <b>
                                <NumberFormatter value={totalAdditionalDeal} />
                            </b>
                            .
                        </List.Item>
                        <List.Item>
                            As a result, your{' '}
                            <b>Additional Secured Revenue (ASR)</b> amounted to{' '}
                            <b>
                                <NumberFormatter prefix="$" value={totalASR} />
                            </b>
                            . This is the{' '}
                            <b>
                                additional revenue your business gained
                                exclusively thanks to your AI Employee
                            </b>{' '}
                            — revenue that would have otherwise been lost.
                        </List.Item>
                        <List.Item>
                            ASR is calculated based on the Lead Value,
                            Additional Deals and other parameters, using the
                            formula: &lt;ASR&gt; = &lt;Lead Value&gt; * &lt;Is
                            Opportunity&gt; * &lt;Opportunity-to-Deal Conversion
                            Rate&gt; * &lt;Result Success Factor&gt; *
                            &lt;Additional Deal Recognition&gt;
                        </List.Item>
                    </List>
                </Collapse>
            </div>
        </Paper>
    );
};

export default Summary;
